import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

export const SearchFieldContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  background: ${({ theme }) => theme.palette.white};
  align-items: center;
  padding: 0 16px;
  // Transparent border preoccupies space for "focused" border, prevents jumpy behavior
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  transition: all 0.2s ease;

  &:focus-within {
    outline: none !important;
    border-color: ${({ theme }) => theme.palette.primary};
    box-shadow: 0 0 0 3px rgba(32, 154, 214, 0.1);
  }

  svg {
    color: ${({ theme }) => theme.palette.grey63};
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  background: transparent;
  outline: none;
  border: 0;
  padding: 12px 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};

  &::placeholder {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.palette.grey63};
  }
`;

export const SuggestionList = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  width: 100%;
  background: ${({ theme }) => theme.palette.white};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scaleY(1);
  transform-origin: top;
  transition: transform 0.2s ease, opacity 0.2s ease;
  opacity: 1;
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 0 5px 5px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

export const SuggestionListItem = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;
  border-bottom: 1px solid #f5f5f5;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(32, 154, 214, 0.08);
  }

  &:active {
    background: rgba(32, 154, 214, 0.15);
    transform: scale(0.99);
  }

  &:focus {
    outline: 2px solid rgba(32, 154, 214, 0.5);
    outline-offset: -2px;
  }

  /* Visual indicator for keyboard navigation */
  &[aria-selected='true'] {
    background: rgba(32, 154, 214, 0.12);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${({ theme }) => theme.palette.primary};
    }
  }
`;

export const ItemSKU = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grey63};
  margin-top: 4px;
`;

export const ItemName = styled.div`
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: 400;

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.palette.primary};
  }
`;

export const Label = styled.label`
  font-size: 14px;
  padding: 12px 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.grey63};
  display: block;
  text-align: center;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  gap: 12px;
`;

export const SkeletonItem = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

export const SkeletonLine = styled.div<{ width?: string; height?: string }>`
  height: ${props => props.height || '16px'};
  width: ${props => props.width || '100%'};
  background: linear-gradient(
    to right,
    #f0f0f0 0%,
    #e8e8e8 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 800px 100px;
  border-radius: 4px;
  animation: ${shimmer} 1.5s infinite linear;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ErrorMessage = styled.div`
  padding: 12px 16px;
  color: #d32f2f;
  font-size: 14px;
  text-align: center;
`;

export const ScreenReaderOnly = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;
